import React, { useMemo, useContext, SyntheticEvent, ReactText } from "react";
import { useTranslation } from "react-i18next";

import { FilterIcon } from "fe-shared/src/components/Icons";
import { MultiSelectInput } from "fe-shared/src/components";
import UserContext from "contexts/userContext";

import { DataGridFilters } from "fe-shared/src/components/DataGrid/modules/types";

import styles from "./AdditionalFilters.module.scss";

interface Props {
  filters: DataGridFilters;
  updateFilters: (filters: DataGridFilters) => void;
  supportedFilters?: FilterName[];
}

export enum FilterName {
  ShowAll = "ShowAll",
  Mine = "Mine",
  ManualFlags = "ManualFlags",
  AutoFlags = "AutoFlags",
  PaperForms = "PaperForms",
  NotLinked = "NotLinked",
}

const AdditionalFilters = (props: Props) => {
  const { filters, updateFilters, supportedFilters } = props;
  const { assignee, hasAutoFlag, hasManualFlag, isPaperForm, isNotLinked } = filters;
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const userName = `${user?.firstName} ${user?.lastName}`;
  const mineChecked = assignee?.value === userName;
  const hasAutoFlagChecked = hasAutoFlag?.value === "true";
  const hasManualFlagChecked = hasManualFlag?.value === "true";
  const isPaperFormChecked = isPaperForm?.value === "true";
  const isNotLinkedChecked = isNotLinked?.value === "true";
  const showAllChecked =
    !mineChecked &&
    !hasAutoFlagChecked &&
    !hasManualFlagChecked &&
    !isPaperFormChecked &&
    !isNotLinkedChecked;
  const filtersCount = [
    mineChecked,
    hasAutoFlagChecked,
    hasManualFlagChecked,
    isPaperFormChecked,
    isNotLinkedChecked,
  ].filter(Boolean).length;

  const value = useMemo(
    () =>
      [
        {
          value: FilterName.ShowAll,
          isChecked: showAllChecked,
        },
        {
          value: FilterName.Mine,
          isChecked: mineChecked,
        },
        {
          value: FilterName.ManualFlags,
          isChecked: hasManualFlagChecked,
        },
        {
          value: FilterName.AutoFlags,
          isChecked: hasAutoFlagChecked,
        },
        {
          value: FilterName.PaperForms,
          isChecked: isPaperFormChecked,
        },
        {
          value: FilterName.NotLinked,
          isChecked: isNotLinkedChecked,
        },
      ]
        .filter((o) => o.isChecked)
        .map((o) => o.value),
    [
      mineChecked,
      hasAutoFlagChecked,
      hasManualFlagChecked,
      showAllChecked,
      isPaperFormChecked,
      isNotLinkedChecked,
    ]
  );

  const options = useMemo(
    () =>
      Object.values(FilterName)
        .filter((name) =>
          supportedFilters ? supportedFilters.includes(name) : true
        )
        .map((name) => ({
          value: name,
          label: t(`transactions.grid.additionalFilters.${name}`),
        })),
    [supportedFilters, t]
  );

  const placeholder = (
    <>
      <FilterIcon fillColor={null} />
      Filters
      {filtersCount ? ` +${filtersCount}` : ""}
    </>
  );

  const onChange = (
    e: SyntheticEvent,
    values: ReactText[],
    selectedValue: ReactText
  ) => {
    switch (selectedValue) {
      case FilterName.Mine:
        updateFilters({
          assignee: {
            type: "textFilter",
            value: mineChecked ? "" : userName,
          },
        });
        break;
      case FilterName.ManualFlags:
        updateFilters({
          hasManualFlag: {
            type: "textFilter",
            value: hasManualFlagChecked ? "" : "true",
          },
        });
        break;
      case FilterName.AutoFlags:
        updateFilters({
          hasAutoFlag: {
            type: "textFilter",
            value: hasAutoFlagChecked ? "" : "true",
          },
        });
        break;
      case FilterName.PaperForms:
        updateFilters({
          isPaperForm: {
            type: "textFilter",
            value: isPaperFormChecked ? "" : "true",
          },
        });
        break;
      case FilterName.NotLinked:
        updateFilters({
          isNotLinked: {
            type: "textFilter",
            value: isNotLinkedChecked ? "" : "true",
          }
        });
        break;
      case FilterName.ShowAll:
        if (!value.includes(FilterName.ShowAll)) {
          updateFilters({
            assignee: {
              type: "textFilter",
              value: "",
            },
            hasManualFlag: {
              type: "textFilter",
              value: "",
            },
            hasAutoFlag: {
              type: "textFilter",
              value: "",
            },
            isPaperForm: {
              type: "textFilter",
              value: "",
            },
            isNotLinked: {
              type: "textFilter",
              value: "",
            },
          });
        }
        break;
      default:
    }
  };

  return (
    <MultiSelectInput
      value={value}
      options={options}
      id="additional-filters-multi-select-input"
      onChange={onChange}
      placeholder={placeholder}
      className={styles.select}
    />
  );
};

export default AdditionalFilters;
